import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { darken, toRgba, getReadableTextColor } from "../utils/colorUtils";

// The confirmed real-app brand blue (sampled from the live screenshots,
// see StudentPortal.css) — the default for every visitor until they pick
// something else; picking a color never changes this default for anyone
// else, it's stored per-browser like any other local preference.
export const DEFAULT_ACCENT_COLOR = "#0070d8";
const STORAGE_KEY = "studentPortal.accentColor";

const PRESET_ACCENT_COLORS = [
  { label: "Ocean Blue", value: "#0070d8" },
  { label: "Indigo", value: "#4f46e5" },
  { label: "Violet", value: "#7c3aed" },
  { label: "Rose", value: "#db2777" },
  { label: "Teal", value: "#0d9488" },
  { label: "Emerald", value: "#16a34a" },
  { label: "Amber", value: "#d97706" },
  { label: "Crimson", value: "#dc2626" },
];

const StudentThemeContext = createContext(null);

const readStoredColor = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

/**
 * Lets the signed-in student pick their own sidebar/button accent color.
 * Nothing is sent to the backend — this is a per-browser preference, same
 * as any other local UI setting. Only the PRIMARY accent is customizable;
 * status colors (success/danger/warning/info) stay fixed so "red" always
 * still means the same thing everywhere in the portal.
 */
export const StudentThemeProvider = ({ children }) => {
  const [accentColor, setAccentColorState] = useState(readStoredColor);

  const setAccentColor = useCallback((hex) => {
    setAccentColorState(hex);
    try {
      localStorage.setItem(STORAGE_KEY, hex);
    } catch {
      // Non-fatal — the color still applies for this session even if it
      // can't be persisted (e.g. private browsing with storage blocked).
    }
  }, []);

  const resetAccentColor = useCallback(() => {
    setAccentColorState(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // See above.
    }
  }, []);

  // Only produce a style override once the student has actually picked a
  // color — otherwise the shell's own stylesheet default (#0070D8) applies
  // untouched, so "leave it as default until someone chooses" just works.
  const themeStyle = useMemo(() => {
    if (!accentColor) return undefined;
    return {
      "--color-primary": accentColor,
      "--color-primary-hover": darken(accentColor, 0.15),
      "--color-primary-text": darken(accentColor, 0.3),
      "--color-primary-soft": toRgba(accentColor, 0.12),
      "--color-on-primary": getReadableTextColor(accentColor),
    };
  }, [accentColor]);

  const value = useMemo(
    () => ({
      accentColor: accentColor || DEFAULT_ACCENT_COLOR,
      isCustomAccent: Boolean(accentColor),
      presets: PRESET_ACCENT_COLORS,
      setAccentColor,
      resetAccentColor,
      themeStyle,
    }),
    [accentColor, setAccentColor, resetAccentColor, themeStyle]
  );

  return <StudentThemeContext.Provider value={value}>{children}</StudentThemeContext.Provider>;
};

export const useStudentTheme = () => {
  const ctx = useContext(StudentThemeContext);
  if (!ctx) throw new Error("useStudentTheme must be used within a StudentThemeProvider");
  return ctx;
};
